import type { ManualAddEstimate } from "@shared/labs/sourcing";
import { Link2, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";

/** Mirrors the server's operational sanity bound (MAX_MANUAL_URLS_PER_CALL). */
const MAX_URLS = 100;

/**
 * Manual URL add — the project's PAID entry path. Each URL costs one
 * single-profile fetch + one judge call. The ESTIMATE is the cost gate: it
 * renders live as the list is pasted and prices the confirm button, so the
 * recruiter always sees the dollar figure they are approving (informed
 * consent — no hard daily ceiling). Rows land as "importando…" (filled by a
 * parallel worker pool) and resolve via the project poll.
 */
export function AddUrlsDialog({
  open,
  onOpenChange,
  projectId,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  /** Called after a successful add so the page refreshes/starts polling. Carries
   *  the ids of URLs already in the project (collisions) for re-triage. */
  onAdded: (skippedCandidateIds: string[]) => void;
}) {
  const { toast } = useToast();
  const [text, setText] = useState("");
  const [estimate, setEstimate] = useState<ManualAddEstimate | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const urls = useMemo(
    () =>
      Array.from(
        new Set(
          text
            .split(/[\n,;\s]+/)
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      ),
    [text],
  );
  const overMax = urls.length > MAX_URLS;

  useEffect(() => {
    if (!open) {
      setText("");
      setEstimate(null);
      return;
    }
    if (urls.length === 0 || overMax) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    rpcClient.labs
      .sourcingProjectManualEstimate({ count: urls.length })
      .then((e: ManualAddEstimate) => {
        if (!cancelled) setEstimate(e);
      })
      .catch(() => {
        if (!cancelled) setEstimate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, urls.length, overMax]);

  async function add() {
    if (urls.length === 0 || overMax || submitting) return;
    setSubmitting(true);
    try {
      const { added, skipped, skippedCandidateIds } =
        await rpcClient.labs.sourcingProjectAddManualUrls({
          projectId,
          urls,
        });
      toast({
        title: `${added} ${added === 1 ? "perfil adicionado" : "perfis adicionados"}`,
        description:
          skipped > 0
            ? `${skipped} já ${skipped === 1 ? "estava" : "estavam"} no projeto — reveja abaixo.`
            : "Buscando e avaliando os perfis — eles aparecem no bloco Sourced em instantes.",
      });
      onOpenChange(false);
      onAdded(skippedCandidateIds);
    } catch (err) {
      toast({
        title: "Falha ao adicionar perfis",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-4 w-4" /> Adicionar por URL do LinkedIn
          </DialogTitle>
          <DialogDescription>
            Cole até {MAX_URLS} URLs de perfil (uma por linha). Cada perfil é importado
            individualmente — uma busca paga por perfil — e avaliado contra o brief do projeto.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            "https://www.linkedin.com/in/exemplo-1\nhttps://www.linkedin.com/in/exemplo-2"
          }
          rows={5}
        />

        <div className="min-h-5 text-xs text-muted-foreground">
          {overMax && (
            <span className="text-destructive">
              Máximo de {MAX_URLS} URLs por vez — remova {urls.length - MAX_URLS}.
            </span>
          )}
          {!overMax && estimate && (
            <span>
              {urls.length} {urls.length === 1 ? "perfil" : "perfis"} · custo estimado{" "}
              <span className="font-medium text-foreground">${estimate.usd.toFixed(2)}</span>
            </span>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={add} disabled={urls.length === 0 || overMax || submitting}>
            {submitting && <Loader2 className="animate-spin" />}
            Importar e avaliar
            {!overMax && estimate && urls.length > 0 && (
              <span className="ml-1 tabular-nums opacity-80">(${estimate.usd.toFixed(2)})</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
